import { inject, Injectable } from '@angular/core';
import { StorageProvider } from '../storage/storage';
import { NetworkProvider } from '../network/network';

import { LoggerFactory } from '../logger/logger-factory.service';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { EquipementControllerApi } from '../../../api/services/equipement-controller-api';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { EquipementDto } from '../../../api/models/equipement-dto';
import { PointMesureControllerApi } from '../../../api/services/point-mesure-controller-api';
import { PointMesureDto } from '../../../api/models/point-mesure-dto';
import { GetPointsMesuresByEquipement$Params } from '../../../api/fn/point-mesure-controller/get-points-mesures-by-equipement';
import { UpsertEquipementQrCode$Params } from '../../../api/fn/equipement-controller/upsert-equipement-qr-code';
import { GetEquipementsByQrCode$Params } from '../../../api/fn/equipement-controller/get-equipements-by-qr-code';
import { SiteEquipementsControllerApi } from '../../../api/services/site-equipements-controller-api';
import { GetEquipementsFromSiteByQrCode$Params } from '../../../api/fn/site-equipements-controller/get-equipements-from-site-by-qr-code';

/**
 * Equipements Provider
 *
 * Ce service permet d'accéder aux service de l'API Equipements
 */
@Injectable({ providedIn: 'root' })
export class EquipementsProvider {
  equipementApi = inject(EquipementControllerApi);
  pointMesureApi = inject(PointMesureControllerApi);
  siteEquipementsApi = inject(SiteEquipementsControllerApi);

  /**
   * Logger
   */
  private logger: LoggerWrapper;

  /**
   * Constructeur
   *
   * Crée une instance de EquipementsProvider.
   *
   * @param networkProvider
   * @param storageProvider
   * @param loggerFactory
   */
  constructor(
    private networkProvider: NetworkProvider,
    private storageProvider: StorageProvider,
    private loggerFactory: LoggerFactory
  ) {
    this.logger = this.loggerFactory.getInstance(this);
  }

  async getPointsMesuresByEquipement(id_equipement: string): Promise<PointMesureDto[]> {
    let params: GetPointsMesuresByEquipement$Params = {
      idEquipement: id_equipement,
    };
    try {
      return await firstValueFrom(this.pointMesureApi.getPointsMesuresByEquipement(params));
    } catch (err) {
      this.logger.error(err, 'error getPointsMesuresByEquipement');
      return null;
    }
  }

  /**
   * Récupération de l'équipement
   */
  async getEquipement(id_equipement: string): Promise<EquipementDto> {
    try {
      if (await this.networkProvider.isReallyConnected()) {
        return await firstValueFrom(
          this.equipementApi.getEquipementById({
            idEquipement: id_equipement,
          })
        );
      } else {
        // let urlToCall = this.apiUrl + '?id_site=' + site_id;
        return await this.storageProvider.get(id_equipement, false);
      }
    } catch (err) {
      this.logger.warn('error getEquipmentsBySite', err);
      return null;
    }
  }
  async getEquipmentsBySite(id_Site: string): Promise<EquipementDto[]> {
    try {
      let equipements: EquipementDto[];
      if (await this.networkProvider.isReallyConnected()) {
        equipements = await firstValueFrom(
          this.equipementApi.getEquipementsBySite({
            id_site: id_Site,
          })
        );
      } else {
        let urlToCall =
          environment.apis.API_DACTECH_BACK + EquipementControllerApi.GetEquipementsBySitePath + '?id_site=' + id_Site;
        equipements = await this.storageProvider.get(this.storageProvider.getStorageName(urlToCall), false);
      }

      return equipements;
    } catch (err) {
      this.logger.warn('error getEquipmentsBySite', err);
      return [];
    }
  }

  async patchEquipement(equipement: EquipementDto): Promise<boolean> {
    try {
      await firstValueFrom(
        this.equipementApi.patchEquipement({
          body: equipement,
        })
      );
      return true;
    } catch (error) {
      console.log('Error patchEquipement', error);
      return false;
    }
  }

  async upsertQrCode(idEquipement: string, qrCode: string): Promise<boolean> {
    try {
      const param: UpsertEquipementQrCode$Params = {
        body: {
          id_equipement: idEquipement,
          qr_code: qrCode,
        },
      };
      await firstValueFrom(this.equipementApi.upsertEquipementQrCode(param));
      return true;
    } catch (error) {
      this.logger.error(error, 'Error upsertQrCode');
      return false;
    }
  }

  async findEquipementByQrCode(qrCode: string): Promise<EquipementDto> {
    const param: GetEquipementsByQrCode$Params = {
      qr_code: qrCode,
    };
    return await firstValueFrom(this.equipementApi.getEquipementsByQrCode(param));
  }

  async findEquipementFromSiteByQrCode(siteId: string, qrCode: string): Promise<EquipementDto> {
    const param: GetEquipementsFromSiteByQrCode$Params = {
      idSite: siteId,
      qr_code: qrCode,
    };

    return await firstValueFrom(this.siteEquipementsApi.getEquipementsFromSiteByQrCode(param));
  }
}
